import { useEffect } from "react";

export const SITE_ORIGIN = "https://thefracturedfive.com";

const setMeta = (selector, attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Per-route head management for a client-rendered app: title, description,
 * canonical, Open Graph / Twitter mirrors, robots directives and optional
 * JSON-LD. Crawlers execute our JS, so this keeps every route distinct.
 */
export const useSeo = ({
  title,
  description,
  path = "/",
  noindex = false,
  image = `${SITE_ORIGIN}/og-image.png`,
  imageAlt = "The Fractured Five — classified series dossier",
  type = "website",
  jsonLd = null,
  jsonLdId = "route-jsonld"
} = {}) => {
  useEffect(() => {
    const url = `${SITE_ORIGIN}${path}`;

    if (title) document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setLink("canonical", url);

    setMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );

    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description
    );
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta(
      'meta[property="og:image:alt"]',
      "property",
      "og:image:alt",
      imageAlt
    );

    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description
    );
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    setMeta(
      'meta[name="twitter:image:alt"]',
      "name",
      "twitter:image:alt",
      imageAlt
    );

    let script = null;
    if (jsonLd) {
      script = document.getElementById(jsonLdId);
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = jsonLdId;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      const stale = document.getElementById(jsonLdId);
      if (stale && jsonLd) stale.remove();
    };
  }, [title, description, path, noindex, image, imageAlt, type, jsonLd, jsonLdId]);
};
